<template>
    <div class="background-small">
        <div class="header-section">
            <h4 class="text-white">Ticket Management</h4>
            <div class="filter-actions">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input type="text" v-model="filters.searchQuery" placeholder="Search tickets...">
                </div>
                <div class="filter-group">
                    <select v-model="filters.status" class="filter-select">
                        <option value="ALL">All Status</option>
                        <option v-for="status in statusOptions" :key="status" :value="status">
                            {{ formatStatus(status) }}
                        </option>
                    </select>
                    <select v-model="filters.priority" class="filter-select">
                        <option value="ALL">All Priority</option>
                        <option v-for="priority in priorityOptions" :key="priority" :value="priority">
                            {{ priority }}
                        </option>
                    </select>
                    <select v-model="filters.type" class="filter-select">
                        <option value="ALL">All Types</option>
                        <option v-for="type in typeOptions" :key="type" :value="type">
                            {{ formatType(type) }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <div class="tickets-container">
            <div class="tickets-list" v-if="tickets?.length > 0">
                <div v-for="ticket in tickets" :key="ticket.id" class="ticket-card">
                    <div class="ticket-header">
                        <div class="ticket-main-info">
                            <div class="ticket-id-date">
                                <span class="ticket-id">#{{ ticket.ticket_number }}</span>
                                <span class="ticket-date">{{ formatDate(ticket.created_at) }}</span>
                            </div>
                            <div class="ticket-title-section">
                                <h6 class="ticket-title">{{ ticket.title }}</h6>
                                <span class="ticket-type" :class="ticket.type.toLowerCase()">
                                    {{ formatType(ticket.type) }}
                                </span>
                            </div>
                            <div class="ticket-user-info">
                                <i class="fas fa-user"></i>
                                <span>{{ ticket.user?.user_name || 'Unknown User' }}</span>
                            </div>
                            <p class="ticket-description-preview">{{ ticket.description }}</p>
                        </div>
                        <div class="ticket-actions">
                            <div class="status-actions">
                                <select v-model="ticket.status" @change="updateTicketStatus(ticket)"
                                    class="status-select" :class="ticket.status.toLowerCase()">
                                    <option v-for="status in statusOptions" :key="status" :value="status">
                                        {{ formatStatus(status) }}
                                    </option>
                                </select>
                                <select v-model="ticket.priority" @change="updateTicketPriority(ticket)"
                                    class="priority-select" :class="ticket.priority.toLowerCase()">
                                    <option v-for="priority in priorityOptions" :key="priority" :value="priority">
                                        {{ priority }}
                                    </option>
                                </select>
                            </div>
                            <div class="action-buttons">
                                <button class="view-btn" @click="viewTicket(ticket)">
                                    <i class="fas fa-eye"></i>
                                    View
                                </button>
                                <button class="delete-btn" @click="deleteTicket(ticket)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="empty-state">
                <i class="fas fa-ticket-alt empty-icon"></i>
                <p>No tickets found</p>
            </div>
        </div>

        <InfoModal v-if="infoModal.show" :duration="infoModal.duration" :title="infoModal.title"
            :message="infoModal.message" :type="infoModal.type" @close="infoModal.show = false" />
    </div>
</template>

<script>
import DateUtils from "@/utils/DateUtils.js";
import SupportService from "@/services/SupportService.js";
import InfoModal from "@/components/modal/InfoModal.vue";
import AdminSupportService from "@/services/admin.SupportService.js";
const TICKET_TYPES = {
    GENERAL: 'General Support',
    PAYMENT: 'Payment Issue',
    TECHNICAL: 'Technical Support',
    BILLING: 'Billing Issue',
    SUPPORT: 'Customer Support',
    COMPLAINT: 'Complaint',
    FEATURE_REQUEST: 'Feature Request',
    BUG_REPORT: 'Bug Report'
};

const TICKET_STATUSES = {
    OPEN: 'Open',
    IN_PROGRESS: 'In Progress',
    WAITING_USER_RESPONSE: 'Waiting for Response',
    PENDING: 'Pending',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled'
};

export default {
    name: 'TicketManagementView',
    components: {
        InfoModal
    },
    data() {
        return {
            tickets: [],
            searchQuery: '',
            filters: {
                searchQuery: '',
                status: 'ALL',
                priority: 'ALL',
                type: 'ALL',
                page: 1,
                limit: 10
            },
            statusOptions: Object.keys(TICKET_STATUSES),
            typeOptions: Object.keys(TICKET_TYPES),
            priorityOptions: ['LOW', 'MEDIUM', 'HIGH', 'URGENT'],
            infoModal: {
                show: false,
                title: '',
                message: '',
                type: 'info',
                duration: "4s",
                durationSeconds: 4000
            }
        }
    },
    methods: {
        formatType(type) {
            return TICKET_TYPES[type] || type;
        },
        formatStatus(status) {
            return TICKET_STATUSES[status] || status;
        },
        formatDate(date) {
            return DateUtils.getDate(date);
        },
        showInfo(title, message, type = 'success') {
            this.infoModal = {
                show: true,
                title,
                message,
                type,
                duration: "4s",
                durationSeconds: 4000
            };
            this.infoModal.show = true;
            setTimeout(() => {
                this.infoModal.show = false;
            }, this.infoModal.durationSeconds);
        },
        async loadTickets() {
            try {
                const res = await AdminSupportService.getTickets({
                    search: this.filters.searchQuery,
                    status: this.filters.status,
                    priority: this.filters.priority,
                    type: this.filters.type,
                    page: this.filters.page,
                    limit: this.filters.limit,
                    include_user: "true"
                });

                if (!res) this.tickets = [];

                this.tickets = res.tickets;
            } catch (error) {
                this.showInfo('Error', 'Failed to load tickets', 'error');
            }
        },
        viewTicket(ticket) {
            this.$router.push(`/admin/tickets/${ticket.id}`);
        },
        async updateTicketStatus(ticket) {
            const response = await SupportService.updateTicketStatus(ticket.id, ticket.status);
            if (!response) return this.showInfo('Error', 'Failed to update ticket status', 'error');

            this.showInfo('Success', 'Ticket status updated successfully');
        },
        async updateTicketPriority(ticket) {
            try {
                const response = await SupportService.updateTicketPriority(ticket.id, ticket.priority);
                if (!response) return this.showInfo('Error', 'Failed to update ticket priority', 'error');

                this.showInfo('Success', 'Ticket priority updated successfully');
            } catch (error) {
                this.showInfo('Error', 'Failed to update ticket priority', 'error');
            }
        },
        async deleteTicket(ticket) {
            if (!confirm('Are you sure you want to delete this ticket?')) return;

            try {
                const response = await SupportService.deleteTicket(ticket.id);
                if (response) {
                    this.tickets = this.tickets.filter(t => t.id !== ticket.id);
                    this.showInfo('Success', 'Ticket deleted successfully');
                } else {
                    this.showInfo('Error', 'Failed to delete ticket', 'error');
                }
            } catch (error) {
                this.showInfo('Error', 'Failed to delete ticket', 'error');
            }
        }
    },
    watch: {
        filters: {
            handler() {
                this.loadTickets();
            },
            deep: true,
        },
    },
    async created() {
        await this.loadTickets();
    }
}
</script>

<style scoped>
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;
}

.filter-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
}

.search-box {
    flex: 1;
    max-width: 350px;
    position: relative;
    display: flex;
    align-items: center;
    color: #666;
}

.search-box input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
}

.filter-group {
    display: flex;
    gap: 0.5rem;
    flex: 1;
    min-width: 200px;
}

.filter-group option {
    color: black;
}

.filter-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
}

.ticket-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    margin-bottom: 1rem;
    padding: 1.2rem;
    transition: all 0.3s ease;
}

.ticket-card:hover {
    background: rgba(255, 255, 255, 0.08);
}

.ticket-header {
    display: flex;
    justify-content: space-between;
    gap: 1.5rem;
}

.ticket-main-info {
    flex: 1;
}

.ticket-id-date {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.ticket-id {
    color: #666;
    font-size: 0.9rem;
}

.ticket-date {
    color: #666;
    font-size: 0.8rem;
}

.ticket-title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.ticket-title {
    color: white;
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
}

.ticket-user-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #999;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
}

.ticket-description-preview {
    color: #999;
    font-size: 0.9rem;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.ticket-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 200px;
}

.status-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.status-select,
.priority-select {
    width: 100%;
    padding: 0.5rem;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    color: white;
    font-size: 0.8rem;
    cursor: pointer;
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
}

.view-btn,
.delete-btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.view-btn {
    background: #3b82f6;
    color: white;
    flex: 1;
}

.view-btn:hover {
    background: #2563eb;
}

.delete-btn {
    background: #ef4444;
    color: white;
    padding: 0.5rem;
}

.delete-btn:hover {
    background: #dc2626;
}

.empty-state {
    text-align: center;
    padding: 3rem;
    color: #666;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
}

@media (max-width: 768px) {
    .header-section {
        padding: 0 0.5rem;
    }

    .filter-actions {
        flex-direction: column;
        gap: 0.75rem;
    }

    .search-box,
    .filter-group {
        width: 100%;
        min-width: unset;
    }

    .ticket-header {
        flex-direction: column;
        gap: 1rem;
    }

    .ticket-actions {
        width: 100%;
        min-width: unset;
    }

    .status-actions {
        flex-direction: row;
        flex-wrap: wrap;
    }

    .status-select,
    .priority-select {
        flex: 1;
        min-width: 150px;
    }

    .action-buttons {
        width: 100%;
    }

    .view-btn,
    .delete-btn {
        width: 100%;
        justify-content: center;
    }

    .ticket-card {
        padding: 1rem;
    }

    .ticket-title {
        font-size: 1rem;
    }

    .ticket-description-preview {
        font-size: 0.85rem;
    }
}

/* Status and Priority Colors */
.status-select.open {
    color: #10b981;
}

.status-select.in_progress {
    color: #f59e0b;
}

.status-select.waiting_user_response {
    color: #0ea5e9;
}

.status-select.pending {
    color: #8b5cf6;
}

.status-select.resolved {
    color: #22c55e;
}

.status-select.closed {
    color: #6b7280;
}

.status-select.cancelled {
    color: #ef4444;
}

.priority-select.urgent {
    color: #ef4444;
}

.priority-select.high {
    color: #f59e0b;
}

.priority-select.medium {
    color: #3b82f6;
}

.priority-select.low {
    color: #6b7280;
}

.ticket-type {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.1);
    color: #ccc;
}
</style>